import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight, Search } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPage = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchPosts();
    }, []);

    const fetchPosts = async () => {
        try {
            const response = await axios.get(`${API}/blog/posts?published_only=true`);
            setPosts(response.data);
        } catch (error) {
            console.error('Error fetching posts:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredPosts = posts.filter(post => 
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    // Placeholder posts for demo
    const placeholderPosts = [
        {
            id: '1',
            slug: 'tax-planning-strategies-2024',
            title: 'Essential Tax Planning Strategies for UK Businesses',
            excerpt: 'Discover effective tax planning strategies that can help reduce your business tax liability while staying fully compliant with HMRC regulations.',
            image_url: 'https://images.unsplash.com/photo-1631248082035-507d0fe8a8e2?crop=entropy&cs=srgb&fm=jpg&q=85',
            author: 'Hope Accountancy',
            created_at: '2024-01-15T10:00:00Z'
        },
        {
            id: '2',
            slug: 'vat-guide-small-businesses',
            title: 'Complete VAT Guide for Small Business Owners',
            excerpt: 'Everything you need to know about VAT registration, returns, and compliance for your small business in the UK.',
            image_url: 'https://images.unsplash.com/photo-1676390377080-b1b08f39c228?crop=entropy&cs=srgb&fm=jpg&q=85',
            author: 'Hope Accountancy',
            created_at: '2024-01-10T10:00:00Z'
        },
        {
            id: '3',
            slug: 'payroll-essentials',
            title: 'Payroll Essentials: What Every Employer Needs to Know',
            excerpt: 'From RTI submissions to pension auto-enrollment, learn the key payroll responsibilities for UK employers.',
            image_url: 'https://images.unsplash.com/photo-1631248055158-edec7a3c072b?crop=entropy&cs=srgb&fm=jpg&q=85',
            author: 'Hope Accountancy',
            created_at: '2024-01-05T10:00:00Z'
        },
    ];

    const displayPosts = posts.length > 0 ? filteredPosts : placeholderPosts;

    return (
        <main data-testid="blog-page">
            {/* Hero Section */}
            <section className="relative pt-32 pb-20 bg-hero-dark overflow-hidden">
                <div className="absolute inset-0 noise-texture" />
                <div className="absolute top-0 right-0 w-96 h-96 bg-hope-soft/20 rounded-full blur-3xl" />
                
                <div className="container-hope relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="max-w-3xl"
                    >
                        <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-white/90 font-medium text-sm mb-6">
                            Blog & Insights
                        </span>
                        <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
                            Financial Insights & Tips
                        </h1>
                        <p className="text-xl text-white/70 leading-relaxed">
                            Expert advice, industry updates, and practical tips to help your business thrive.
                        </p>
                    </motion.div>
                </div>
                
                <div className="absolute bottom-0 left-0 right-0">
                    <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F1F8F4"/>
                    </svg>
                </div>
            </section>

            {/* Blog List */}
            <section className="section-padding bg-hope-light">
                <div className="container-hope">
                    {/* Search Bar */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="max-w-xl mx-auto mb-16"
                    >
                        <div className="relative">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search articles..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                data-testid="blog-search-input"
                                className="w-full pl-12 pr-4 py-4 rounded-2xl border border-gray-200 focus:border-hope-emerald focus:ring-2 focus:ring-hope-emerald/20 outline-none transition-all bg-white"
                            />
                        </div>
                    </motion.div>

                    {/* Posts Grid */}
                    {loading ? (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="bg-white rounded-3xl overflow-hidden">
                                    <div className="h-56 skeleton" />
                                    <div className="p-6 space-y-4">
                                        <div className="h-6 skeleton rounded-lg w-3/4" />
                                        <div className="h-4 skeleton rounded-lg w-full" />
                                        <div className="h-4 skeleton rounded-lg w-2/3" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : displayPosts.length === 0 ? (
                        <div className="text-center py-16">
                            <p className="text-gray-500 text-lg">No articles found matching your search.</p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {displayPosts.map((post, index) => (
                                <motion.article
                                    key={post.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                    data-testid={`blog-card-${index}`}
                                    className="blog-card group"
                                >
                                    <div className="overflow-hidden">
                                        <img
                                            src={post.image_url || 'https://images.unsplash.com/photo-1631248082035-507d0fe8a8e2?crop=entropy&cs=srgb&fm=jpg&q=85'}
                                            alt={post.title}
                                            className="w-full h-56 object-cover"
                                        />
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                                            <span className="flex items-center gap-1.5">
                                                <Calendar className="w-4 h-4" />
                                                {formatDate(post.created_at)}
                                            </span>
                                            <span className="flex items-center gap-1.5">
                                                <User className="w-4 h-4" />
                                                {post.author}
                                            </span>
                                        </div>
                                        <h2 className="font-heading font-semibold text-xl text-hope-forest mb-3 group-hover:text-hope-soft transition-colors">
                                            {post.title}
                                        </h2>
                                        <p className="text-gray-600 mb-4 line-clamp-2">
                                            {post.excerpt}
                                        </p>
                                        <Link
                                            to={`/blog/${post.slug}`}
                                            className="inline-flex items-center gap-2 text-hope-emerald font-medium group-hover:gap-3 transition-all"
                                        >
                                            Read More <ArrowRight className="w-4 h-4" />
                                        </Link>
                                    </div>
                                </motion.article>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </main>
    );
};

export default BlogPage;
